import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppAuthData } from '@/stores/app/authData';
import { useHighlights } from '@/stores/highlights/highlights';
import { useDocumentEditor } from '@/stores/document/editor';
import { useEditor } from '@/stores/editor/editor';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import { buildRequiredFieldGuidanceMessages } from '@/helpers/jobProfileGuidanceMessages';
import {
  getJobProfile, getJobProfileRevisions, getJobProfileVersions, getJobProfileRevision, getJobProfileHris,
  rescoreJobProfile, finalizeJobProfileTemplate, applyJobProfileTemplate, getProfilesByTemplate,
  deleteJobProfile as deleteJobProfileRequest, restoreJobProfileRevision, duplicateJobProfile,
  linkHrisProfile as linkHrisProfileRequest,
} from '@/api/jobProfile';
import { generateJobProfileSummary, generateJobProfile } from '@/api/dfd';

export const useCurrentJobProfile = defineStore('currentJobProfile', () => {
  const { setLoading } = useAppLoading();
  const { checkRbacPermissions } = useAppAuthData();

  const isRescoring = ref(false);
  const rescorePending = ref(false);
  const isUpdated = ref(false);
  const jobProfile = ref(null);
  const isHrisProfile = ref(false);
  const editorContents = ref({});

  // ignore content updates for 1 second after a load
  const rescoreCoolDown = ref(true);

  const profileRevisions = ref([]);
  const sideRevision = ref(null);
  const sideRevisionLoading = ref(false);
  const templateProfiles = ref([]);

  const startRescoreCoolDown = () => {
    rescoreCoolDown.value = true;
    setTimeout(() => {
      rescoreCoolDown.value = false;
    }, 1000);
  };

  const resetJobProfile = () => {
    jobProfile.value = null;
    isHrisProfile.value = false;
    templateProfiles.value = [];
    profileRevisions.value = [];
    editorContents.value = {};
    isUpdated.value = false;
    isRescoring.value = false;
    rescorePending.value = false;
    startRescoreCoolDown();
  };

  let rescoreTimer = null;

  const toLegacyFields = (fields = []) => fields.reduce((res, field) => {
    if (field.header && field.header.length > 0) {
      res.push({
        id: `${field.type}__header`,
        label: '',
        sections: [{ blocks: field.header }],
      });
    }
    res.push({
      id: field.type,
      label: field.label,
      sections: field.sections || [{ blocks: field.blocks }],
    });
    if (field.footer && field.footer.length > 0) {
      res.push({
        id: `${field.type}__footer`,
        label: '',
        sections: [{ blocks: field.footer }],
      });
    }
    return res;
  }, []);

  const loadJobProfile = async ({ ulid, isHris = false, rescore = false }) => {
    if (!ulid) {
      resetJobProfile();
      return;
    }

    setLoading({ pageLoading: true });
    useHighlights().updateBodyMessages({});

    try {
      const profile = await (isHris ? getJobProfileHris : getJobProfile)({ ulid, rescore });
      jobProfile.value = profile;
      isUpdated.value = false;
      isHrisProfile.value = isHris;

      useHighlights().updateBodyMessages({
        bodyMessages: profile.messages,
        fields: toLegacyFields(profile.fields),
        title: profile.title,
        location: profile.location,
      });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile load error: ${err.message || err}`,
      });
      jobProfile.value = null;
      throw err;
    } finally {
      setLoading({ pageLoading: false });
      startRescoreCoolDown();
    }
  };

  const isTemplate = computed(() => jobProfile.value?.type_key === 'job_profile_template');

  const hasPendingApprovalFlows = computed(() => jobProfile.value?.approval_flow_counts?.pending > 0);
  const showPendingApprovalAlert = computed(() => hasPendingApprovalFlows.value
    && checkRbacPermissions(['saveDraftProfileRevision']));

  const readOnlyDraft = computed(() => !isHrisProfile.value && !checkRbacPermissions(['saveDraftProfileRevision']));
  const readOnlyTemplate = computed(() => isTemplate.value && !checkRbacPermissions(['createProfileTemplate']));
  const readOnly = computed(() => readOnlyDraft.value
    || readOnlyTemplate.value
    || isHrisProfile.value
    || hasPendingApprovalFlows.value);

  const fields = computed(() => {
    const jobFields = jobProfile.value?.fields || [];

    return jobFields.map((field) => {
      const blocks = typeof field.blocks === 'string' ? [{
        type: 'paragraph',
        html: field.blocks,
      }] : field.blocks;

      const headerBlocks = Array.isArray(field.header) ? field.header : [];
      const footerBlocks = Array.isArray(field.footer) ? field.footer : [];

      const nodes = field.formatBlacklist.reduce((res, tag) => ({ ...res, [tag]: true }), {});

      return {
        ...field,
        id: field.id || field.type,
        blocks,
        html: blocks?.map((block) => block.html).join(''),
        headerHtml: headerBlocks.map((b) => b.html).join(''),
        footerHtml: footerBlocks.map((b) => b.html).join(''),
        disabledNodes: {
          paragraph: nodes.P,
          heading: nodes.H1 && nodes.H2 && nodes.H3,
          h1: nodes.H1,
          h2: nodes.H2,
          h3: nodes.H3,
          list: nodes.UL,
          orderedList: nodes.OL,
          link: nodes.A,
          bold: nodes.STRONG,
          italic: nodes.EM,
        },
      };
    });
  });

  const summaryField = computed(() => fields.value?.find((f) => f.content_generator_type === 'summary') ?? null);
  const summaryFieldId = computed(() => summaryField.value?.id ?? null);

  const nonEmptyFields = computed(() => fields.value.filter(
    (field) => field.blocks?.some((block) => block.html?.trim?.()),
  ));

  const getFieldHtml = (key) => {
    const field = editorContents.value?.[key] || {};
    return [field.header, field.content, field.footer].join('');
  };

  const bodyMessages = computed(() => {
    const profileMessages = jobProfile.value?.bodyMessages || {};
    return profileMessages;
  });

  const loadRevisions = async () => {
    if (!jobProfile.value?.ulid) return;

    try {
      setLoading({ listLoading: true });
      if (isHrisProfile.value) {
        const data = await getJobProfileVersions({ ulid: jobProfile.value.ulid });
        profileRevisions.value = (data?.versions || []).map((version) => ({
          ...version,
          created: version.effective_date,
        }));
      } else {
        const data = await getJobProfileRevisions({ ulid: jobProfile.value.ulid });
        profileRevisions.value = data?.revisions || [];
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile revisions load error: ${err.message || err}`,
      });
      profileRevisions.value = [];
      throw err;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const loadProfileRevision = async ({ revisionUlid }) => {
    sideRevisionLoading.value = true;
    sideRevision.value = null;
    try {
      if (isHrisProfile.value) {
        let version = profileRevisions.value.find((r) => r.ulid === revisionUlid);
        if (!version) {
          await loadRevisions();
          version = profileRevisions.value.find((r) => r.ulid === revisionUlid);
        }
        sideRevision.value = version || null;
      } else {
        const { profile = {} } = await getJobProfileRevision({ revisionUlid });
        sideRevision.value = profile;
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile revision load error: ${err.message || err}`,
      });
      throw err;
    } finally {
      sideRevisionLoading.value = false;
    }
  };

  const loadProfilesByTemplate = async () => {
    if (!jobProfile.value?.ulid) return;

    try {
      setLoading({ listLoading: true });
      const data = await getProfilesByTemplate({ ulid: jobProfile.value.ulid });
      templateProfiles.value = data?.profiles || [];
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Template profiles load error: ${err.message || err}`,
      });
      templateProfiles.value = [];
      throw err;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const revisions = computed(() => {
    const today = new Date().toLocaleDateString();
    const revisionList = profileRevisions.value.filter((revision, i) => {
      if (i === 0) return true;

      const revisionDay = revision.created ? new Date(revision.created).toLocaleDateString() : null;
      if (revisionDay === today) return true;
      if (revision.revision_name) return true;

      const prevRevision = profileRevisions.value[i - 1];
      const prevDay = prevRevision?.created ? new Date(prevRevision.created).toLocaleDateString() : null;
      if (revision.createdBy !== prevRevision.createdBy) return true;
      if (revisionDay !== prevDay) return true;

      return false;
    });

    const seenQuarters = new Set();
    return revisionList
      .map((revision) => {
        let quarterLabel = '';
        let dateString = '';
        if (revision.created) {
          const date = new Date(revision.created);
          dateString = date.toLocaleString();
          const year = date.getFullYear();
          const quarter = Math.ceil((date.getMonth() + 1) / 3);

          if (!seenQuarters.has(`${year}-Q${quarter}`)) {
            quarterLabel = `Q${quarter} ${year}`;
            seenQuarters.add(`${year}-Q${quarter}`);
          }
        }
        return {
          ...revision,
          id: revision.ulid,
          created: dateString,
          ...(quarterLabel && { quarter: quarterLabel }),
        };
      });
  });

  const rescorePayload = computed(() => {
    const profile = jobProfile.value;
    if (!profile) return null;

    const contents = editorContents.value;

    const fieldsMap = {};
    fields.value.forEach((field) => {
      const entry = contents[field.id] || {};
      fieldsMap[field.id] = entry.content || field.html;

      if (entry.header || field.headerHtml) {
        fieldsMap[`${field.id}__header`] = entry.header || field.headerHtml;
      }
      if (entry.footer || field.footerHtml) {
        fieldsMap[`${field.id}__footer`] = entry.footer || field.footerHtml;
      }
    });

    return {
      title: contents.title?.content,
      fields: fieldsMap,
      jobCode: contents.jobCode?.content,
      company: contents.company?.content,
      location: contents.location?.content,
      jobLevelUlid: jobProfile.value?.job_level_ulid,
    };
  });

  const doRescore = async () => {
    isRescoring.value = true;

    try {
      const profile = await rescoreJobProfile({
        ulid: jobProfile.value?.ulid,
        payload: rescorePayload.value,
      });

      jobProfile.value = profile;

      useHighlights().updateBodyMessages({
        bodyMessages: profile.messages,
        fields: toLegacyFields(profile.fields),
        title: profile.title,
        location: profile.location,
        isRescore: true,
      });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile rescore error: ${err.message || err}`,
      });
      throw err;
    } finally {
      rescorePending.value = false;
      isRescoring.value = false;
    }
  };

  const scheduleRescore = () => {
    if (isHrisProfile.value || rescoreCoolDown.value) {
      return;
    }

    rescorePending.value = true;

    clearTimeout(rescoreTimer);
    rescoreTimer = setTimeout(doRescore, 2000);
  };

  const updateEditorContent = ({ editorId, part = 'content', html }) => {
    let baseId = editorId;
    let fieldPart = part;
    if (editorId.endsWith('__header') || editorId.endsWith('__footer')) {
      [baseId, fieldPart] = editorId.split('__');
    }

    const prev = editorContents.value[baseId];
    const prevObj = (prev && typeof prev === 'object') ? prev : {};

    // if the content is the same, don't rescore
    if (prevObj[fieldPart] === html) {
      return;
    }

    editorContents.value = { ...editorContents.value, [baseId]: { ...prevObj, [fieldPart]: html } };
    scheduleRescore();
  };

  const updateJobLevelUlid = (ulid) => {
    jobProfile.value.job_level_ulid = ulid;
    scheduleRescore();
  };

  const generateProfileJobDescription = (mode = 'replace', selectedFieldIds = null) => {
    const jobDescriptionField = fields.value.find((f) => f.is_job_description);
    if (!jobDescriptionField) return;

    const combined = fields.value
      .filter((f) => !f.is_job_description)
      .filter((f) => !selectedFieldIds || selectedFieldIds.includes(f.id))
      .map((f) => {
        const editor = editorContents.value[f.id] || {};
        const header = editor.header || '';
        const content = editor.content || '';
        const footer = editor.footer || '';
        const parts = [header, content, footer].filter((s) => !isEmptyHtml(s));

        if (!parts.length) return '';
        return [`<h3>${f.label}</h3>`, ...parts].join('\n');
      })
      .filter(Boolean)
      .join('\n');

    const tiptap = useDocumentEditor().tiptap[jobDescriptionField.id];
    if (tiptap) {
      const html = mode === 'append'
        ? `${tiptap.getHTML()}\n${combined}`
        : combined;
      tiptap.chain()
        .setMeta('addToHistory', false)
        .setContent(html, true, { preserveWhitespace: true })
        .run();
      setTimeout(() => { tiptap.emit('update', {}); }, 100);
    }
  };

  const generateFieldContent = async (field) => {
    const isSummary = field.content_generator_type === 'summary';

    const firstDraft = useFirstDraft();
    firstDraft.isNewContentLoading = true;
    firstDraft.newContentLoadingStatus = isSummary ? 'Generating summary...' : 'Generating content...';
    try {
      const { summary, fieldType, profile = {} } = isSummary
        ? await generateJobProfileSummary(jobProfile.value?.ulid)
        : await generateJobProfile(
          jobProfile.value?.ulid,
          { useExistingSummary: !isEmptyHtml(getFieldHtml(summaryFieldId.value)) },
        );

      const targetId = fieldType || field.id;
      const content = isSummary ? summary : profile[targetId];

      const tiptap = useDocumentEditor().tiptap[targetId];
      if (tiptap) {
        tiptap.chain()
          .setMeta('addToHistory', false)
          .setContent(content, true, { preserveWhitespace: true })
          .run();
        setTimeout(() => { tiptap.emit('update', {}); }, 100);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to generate content: ${err.message || err}` });
      throw err;
    } finally {
      firstDraft.isNewContentLoading = false;
      firstDraft.newContentLoadingStatus = '';
    }
  };

  const generateSummaryContent = () => {
    if (summaryField.value) generateFieldContent(summaryField.value);
  };

  const isAlreadyFinalized = computed(() => (
    jobProfile.value?.finalized_revision_ulid === jobProfile.value?.last_revision_ulid
  ));

  const profileGuidanceMessages = computed(() => {
    const templateMessage = {
      severity_class: 'red-underline',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Apply Template',
      tooltip_long: 'Use a levelling template to get started',
      suggestions: [],
    };
    const templateKudoMessage = {
      severity_class: 'green-solid',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Template Applied',
      suggestions: [],
    };

    const titleKudoMessage = {
      severity_class: 'green-solid',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Job Title Added',
      suggestions: [],
    };

    const missingJobCodeMessage = {
      severity_class: 'red-underline',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Add a Job Code',
      suggestions: [],
    };
    const missingJobCodeKudoMessage = {
      severity_class: 'green-solid',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Job Code Added',
      suggestions: [],
    };

    const jobCodeMismatchMessage = {
      severity_class: 'red-underline',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Job code does not match Workday source',
      suggestions: [],
    };

    const jobCodeMismatch = jobProfile.value?.hris_reference
      && jobProfile.value?.job_code
      && jobProfile.value?.hris_reference?.job_code !== jobProfile.value?.job_code;

    return {
      ...jobProfile.value?.bodyMessages,

      ...(!isTemplate.value && !isHrisProfile.value && {
        guidance_template: jobProfile.value?.last_applied_template_ulid ? templateKudoMessage : templateMessage,
      }),
      ...(jobProfile.value?.title && { guidance_title: titleKudoMessage }),
      ...buildRequiredFieldGuidanceMessages(fields.value, getFieldHtml, isEmptyHtml),
      ...(!isTemplate.value && {
        guidance_job_code: !jobProfile.value?.job_code ? missingJobCodeMessage : missingJobCodeKudoMessage,
      }),
      ...(jobCodeMismatch ? { guidance_job_code_mismatch: jobCodeMismatchMessage } : {}),
    };
  });

  const finalizeTemplate = async () => {
    setLoading({ modalLoading: true });
    try {
      const { template } = await finalizeJobProfileTemplate({ ulid: jobProfile.value?.ulid });
      jobProfile.value = template;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Finalize template error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const applyTemplate = async ({ templateUlid }) => {
    setLoading({ modalLoading: true });
    try {
      const {
        document_ulid: documentUlid, revision_ulid: revisionUlid,
      } = await applyJobProfileTemplate({ ulid: jobProfile.value?.ulid, templateUlid });

      return { documentUlid, revisionUlid, oldRevisionUlid: jobProfile.value?.last_revision_ulid };
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Apply template error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const deleteJobProfile = async () => {
    const ulid = jobProfile.value?.ulid;
    if (!ulid) {
      return;
    }

    setLoading({ pageLoading: true });
    try {
      await deleteJobProfileRequest({ ulid });
      useAppSnackbar().addSuccessSnackbar({
        text: `${jobProfile.value?.title || 'Job'} has been deleted`,
      });
      resetJobProfile();
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Delete job profile error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const createDraftFromHrisProfile = async () => {
    setLoading({ modalLoading: true });
    try {
      const { document_ulid: documentUlid } = await duplicateJobProfile({ ulid: jobProfile.value?.ulid });
      return documentUlid;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to create draft: ${err.message || err}` });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const restoreRevision = async ({ revisionUlid: explicitRevisionUlid } = {}) => {
    const ulid = jobProfile.value?.ulid;
    const revisionUlid = explicitRevisionUlid || sideRevision.value?.revision_ulid;
    if (!ulid || !revisionUlid) {
      return;
    }
    const revision = revisions.value.find((r) => r.ulid === revisionUlid);
    const revisionName = revision ? `Restored revision of ${revision.created}` : '';

    setLoading({ pageLoading: true });
    try {
      const { profile = {} } = await restoreJobProfileRevision({ ulid, revisionUlid, revisionName });
      jobProfile.value = profile;
      useHighlights().updateBodyMessages({
        bodyMessages: profile.messages,
        fields: toLegacyFields(profile.fields),
        title: profile.title,
        location: profile.location,
      });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Restore revision error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const linkHrisProfile = async (hrisUlid) => {
    setLoading({ modalLoading: true });
    try {
      await linkHrisProfileRequest({
        draftUlid: jobProfile.value.ulid,
        hrisUlid,
      });
      const updated = await getJobProfile({ ulid: jobProfile.value.ulid, rescore: true });
      jobProfile.value.hris_matching = updated.hris_matching;
      jobProfile.value.hris_reference = updated.hris_reference;
      jobProfile.value.hris_reference_ulid = updated.hris_reference_ulid;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Failed to link to HRIS profile: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const unlinkDraftProfile = async (draftUlid) => {
    setLoading({ modalLoading: true });
    try {
      await linkHrisProfileRequest({ draftUlid, hrisUlid: null });
      const updated = await getJobProfileHris({ ulid: jobProfile.value.ulid, rescore: true });
      jobProfile.value.draft_profiles = updated.draft_profiles ?? [];
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Failed to unlink draft profile: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const copyAllFieldsToClipboard = (title) => {
    const content = fields.value.map((field) => {
      const fieldLabel = `<h2>${field.label}</h2>`;
      const fieldHeader = isEmptyHtml(field.headerHtml) ? '' : field.headerHtml;
      const fieldBody = isEmptyHtml(field.html) ? '' : field.html;
      const fieldFooter = isEmptyHtml(field.footerHtml) ? '' : field.footerHtml;
      const fieldContent = `${fieldHeader}${fieldBody}${fieldFooter}`;
      return fieldContent.length ? `${fieldLabel}${fieldContent}` : '';
    });

    const fullContent = `<h1>${title}</h1>${content.join('')}`;

    useEditor().copyToClipboard({ content: fullContent, isHtml: true });
  };

  return {
    // State
    isRescoring,
    rescorePending,
    isUpdated,
    jobProfile,
    fields,
    summaryField,
    summaryFieldId,
    nonEmptyFields,
    bodyMessages,
    profileGuidanceMessages,
    revisions,
    templateProfiles,
    isHrisProfile,
    isTemplate,
    isAlreadyFinalized,
    editorContents,
    sideRevision,
    sideRevisionLoading,
    readOnly,
    readOnlyTemplate,
    readOnlyDraft,
    hasPendingApprovalFlows,
    showPendingApprovalAlert,

    // Actions
    loadJobProfile,
    resetJobProfile,
    loadRevisions,
    loadProfileRevision,
    loadProfilesByTemplate,
    updateEditorContent,
    finalizeTemplate,
    applyTemplate,
    deleteJobProfile,
    generateProfileJobDescription,
    generateFieldContent,
    generateSummaryContent,
    restoreRevision,
    createDraftFromHrisProfile,
    updateJobLevelUlid,
    linkHrisProfile,
    unlinkDraftProfile,
    copyAllFieldsToClipboard,
  };
});
